import api from './api';
import { API_BASE_URL } from './api';

export interface BugReport {
  id: number;
  title: string;
  description: string;
  status: 'Zgłoszono' | 'Przetestować' | 'Rozwiązano' | 'Nie działa';
  reporter_user_id: string;
  reporter_name?: string;
  screenshots: string[];
  created_at: string;
  updated_at: string;
}

// Funkcja do normalizacji URL obrazów (tak jak w chatach)
const normalizeImageUrl = (url: string): string => {
  if (!url) return url;
  // Jeśli już jest pełny URL, zwróć go
  if (/^https?:\/\//i.test(url)) return url;
  // Dodaj bazowy URL API (tak jak w internalChatService.ts)
  const base = (api as any).defaults?.baseURL || API_BASE_URL || 'http://localhost:5000/api';
  const apiBase = base.replace(/\/?$/, '');
  // URL z backendu to /uploads/bug-reports/..., więc dodajemy apiBase
  // Upewniamy się, że URL zaczyna się od /, jeśli nie, dodajemy go
  const normalizedUrl = url.startsWith('/') ? url : '/' + url;
  return apiBase + normalizedUrl;
};

// Funkcja do denormalizacji URL - zamienia pełny URL na względny przed zapisem do bazy
const denormalizeImageUrl = (url: string): string => {
  if (!url) return url;
  // Jeśli już jest względny URL, zwróć go
  if (url.startsWith('/uploads/')) return url;
  // Jeśli jest pełny URL, usuń bazowy adres API
  const base = (api as any).defaults?.baseURL || API_BASE_URL || 'http://localhost:5000/api';
  const apiBase = base.replace(/\/?$/, '');
  if (url.startsWith(apiBase)) {
    return url.slice(apiBase.length) || '/';
  }
  return url;
};

export interface CreateBugReportPayload {
  title: string;
  description: string;
  screenshots: string[];
}

class BugReportService {
  async createBugReport(payload: CreateBugReportPayload): Promise<BugReport> {
    // Denormalizuj URL przed wysłaniem do backendu (zapisz względne ścieżki)
    const denormalizedPayload = {
      ...payload,
      screenshots: payload.screenshots.map(url => denormalizeImageUrl(url))
    };
    const res = await api.post<{ success: boolean; data: BugReport }>('/bug-reports', denormalizedPayload);
    if (!res.data.success || !res.data.data) {
      throw new Error('Nie udało się utworzyć zgłoszenia błędu');
    }
    const report = res.data.data;
    return {
      ...report,
      screenshots: report.screenshots.map(url => normalizeImageUrl(url))
    };
  }

  async listBugReports(params?: {
    status?: 'Zgłoszono' | 'Przetestować' | 'Rozwiązano' | 'Nie działa';
    reporter_user_id?: string;
    search?: string;
  }): Promise<BugReport[]> {
    const res = await api.get<{ success: boolean; data: BugReport[] }>('/bug-reports', { params });
    const reports = res.data.data || [];
    // Normalizuj URL screenshotów
    return reports.map(report => ({
      ...report,
      screenshots: (report.screenshots || []).map(url => normalizeImageUrl(url))
    }));
  }

  async getBugReport(id: number): Promise<BugReport> {
    const res = await api.get<{ success: boolean; data: BugReport }>(`/bug-reports/${id}`);
    if (!res.data.success || !res.data.data) {
      throw new Error('Nie udało się pobrać zgłoszenia błędu');
    }
    const report = res.data.data;
    // Normalizuj URL screenshotów
    return {
      ...report,
      screenshots: report.screenshots.map(url => normalizeImageUrl(url))
    };
  }

  async updateBugReport(id: number, payload: CreateBugReportPayload): Promise<BugReport> {
    // Denormalizuj URL przed wysłaniem do backendu (zapisz względne ścieżki)
    const denormalizedPayload = {
      ...payload,
      screenshots: payload.screenshots.map(url => denormalizeImageUrl(url))
    };
    const res = await api.put<{ success: boolean; data: BugReport }>(`/bug-reports/${id}`, denormalizedPayload);
    if (!res.data.success || !res.data.data) {
      throw new Error('Nie udało się zaktualizować zgłoszenia błędu');
    }
    const report = res.data.data;
    return {
      ...report,
      screenshots: report.screenshots.map(url => normalizeImageUrl(url))
    };
  }

  async updateBugReportStatus(id: number, status: 'Zgłoszono' | 'Przetestować' | 'Rozwiązano' | 'Nie działa'): Promise<BugReport> {
    const res = await api.patch<{ success: boolean; data: BugReport }>(`/bug-reports/${id}/status`, { status });
    if (!res.data.success || !res.data.data) {
      throw new Error('Nie udało się zaktualizować statusu');
    }
    const report = res.data.data;
    return {
      ...report,
      screenshots: report.screenshots.map(url => normalizeImageUrl(url))
    };
  }

  async deleteBugReport(id: number): Promise<void> {
    const res = await api.delete<{ success: boolean; message?: string }>(`/bug-reports/${id}`);
    if (!res.data.success) {
      throw new Error(res.data.message || 'Nie udało się usunąć zgłoszenia błędu');
    }
  }

  async uploadScreenshot(file: File): Promise<string> {
    const form = new FormData();
    form.append('image', file);
    const res = await api.post<{ success: boolean; data: { url: string } }>('/bug-reports/upload-screenshot', form, {
      headers: { 'Content-Type': 'multipart/form-data' }
    });
    if (!res.data.success || !res.data.data?.url) {
      throw new Error('Nie udało się przesłać obrazu');
    }
    // Zwróć znormalizowany URL (pełny URL z bazowym adresem API)
    return normalizeImageUrl(res.data.data.url);
  }

  async getBugReportHistory(id: number): Promise<BugReportHistoryItem[]> {
    const res = await api.get<{ success: boolean; data: BugReportHistoryItem[] }>(`/bug-reports/${id}/history`);
    if (!res.data.success || !res.data.data) {
      throw new Error('Nie udało się pobrać historii zgłoszenia');
    }
    return res.data.data;
  }

  async getBugReportComments(id: number): Promise<BugReportComment[]> {
    const res = await api.get<{ success: boolean; data: BugReportComment[] }>(`/bug-reports/${id}/comments`);
    if (!res.data.success || !res.data.data) {
      throw new Error('Nie udało się pobrać komentarzy zgłoszenia');
    }
    return res.data.data;
  }

  async addBugReportComment(id: number, comment_text: string): Promise<BugReportComment> {
    const res = await api.post<{ success: boolean; data: BugReportComment }>(`/bug-reports/${id}/comments`, { comment_text });
    if (!res.data.success || !res.data.data) {
      throw new Error('Nie udało się dodać komentarza');
    }
    return res.data.data;
  }
}

export interface BugReportHistoryItem {
  id: number;
  bug_report_id: number;
  user_id: string;
  action_type: 'status_changed' | 'comment_added' | 'created';
  old_status?: string;
  new_status?: string;
  comment_text?: string;
  created_at: string;
  user_name?: string;
  user_email?: string;
}

export interface BugReportComment {
  id: number;
  bug_report_id: number;
  user_id: string;
  comment_text: string;
  created_at: string;
  updated_at: string;
  user_name?: string;
  user_email?: string;
  user_avatar_url?: string;
}

const bugReportService = new BugReportService();
export default bugReportService;

